/* eslint react-hooks/exhaustive-deps: "warn" */

import { useRef } from 'react';

import cn from 'classnames';

import { useInt } from 'hooks/useInt';
import { useIntersection } from 'hooks/useIntersection';

import { Icon } from 'ui/atoms/Icon/Icon';
import { TitleBlock } from 'ui/atoms/TitleBlock/TitleBlock';
import { Section } from 'ui/molecules/Section/Section';

import styles from './WhoUsesItSection.module.scss';

type Translate = (key: string) => string;

type WhoItem = {
  n: string;
  icon: string;
  role: string;
  headline: string;
  tags: string[];
};

const getWhoItems = (t: Translate): WhoItem[] => [
  {
    n: '01',
    icon: 'marketing',
    role: t('Sales and marketing'),
    headline: t(
      'Qualify an account on the page you found it on, then write the company and the contact to your CRM before the tab closes.',
    ),
    tags: ['SDR', t('Account executive'), 'RevOps'],
  },
  {
    n: '02',
    icon: 'payg-financial-data',
    role: t('Investors and analysts'),
    headline: t(
      'Read twenty years of filed financials and the full ownership chain in a first pass, without opening a registry portal for every jurisdiction.',
    ),
    tags: [t('Deal team'), t('Research'), t('Diligence')],
  },
  {
    n: '03',
    icon: 'payg-account-scoring',
    role: t('Credit and procurement'),
    headline: t(
      'Check registration, VAT, credit position, judgments and outstanding charges before onboarding a supplier or extending payment terms.',
    ),
    tags: [t('Credit control'), t('Supplier onboarding')],
  },
  {
    n: '04',
    icon: 'payg-risk-compliance',
    role: t('Risk and compliance'),
    headline: t(
      'Establish the legal entity, its registered address and its ownership chain at first contact, so the file starts from the register.',
    ),
    tags: ['KYB', t('Onboarding'), t('Screening')],
  },
  {
    n: '05',
    icon: 'rocket',
    role: t('Founders and operators'),
    headline: t(
      'Check a partner, a supplier or a competitor as you come across them, using the registry record rather than the marketing site.',
    ),
    tags: [t('Partnerships'), t('Competitive')],
  },
];

export const WhoUsesItSection = () => {
  const { t } = useInt();
  const ref = useRef<HTMLDivElement>(null);
  const revealed = useIntersection(ref, { threshold: 0.15 });

  return (
    <Section className={styles.root}>
      <div className={styles.grid}>
        <div ref={ref} className={cn(styles.head, revealed && styles.in)}>
          <TitleBlock
            align="left"
            title={t('Anyone who needs the filed answer while they are still on the page.')}
            description={t(
              'One panel, five jobs. The record is the same in every case — what changes is which part of it you act on.',
            )}
          />
        </div>

        <ol className={styles.list}>
          {getWhoItems(t).map((item, index) => (
            <li
              key={item.role}
              className={cn(styles.row, revealed && styles.in)}
              style={{ transitionDelay: revealed ? `${index * 0.08}s` : '0s' }}
            >
              <span className={styles.rowAccent} aria-hidden="true" />

              <span className={styles.num}>{item.n}</span>

              <span className={styles.icon}>
                <Icon type={item.icon} size="md" />
              </span>

              <div className={styles.rowBody}>
                <p className={styles.role}>{item.role}</p>
                <p className={styles.text}>{item.headline}</p>

                <div className={styles.tags}>
                  {item.tags.map((tag) => (
                    <span key={tag} className={styles.tag}>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </Section>
  );
};
